<!--
  The feature story, told as the app's own information architecture.

  The old site showed a nine card grid. Nine equal cards teach nothing about
  how the product is arranged, and someone who downloads after reading them
  still has to learn the app from scratch.

  These four blocks are the four tabs, in the order the app puts them. By the
  time a reader reaches the bottom they already know where things live. Each
  screen is drawn from the design system rather than photographed, so it
  re-themes, re-translates and cannot go stale.
-->
<section id="features" class="bg-ground py-[112px]">
  <div class="mx-auto max-w-[1180px] px-8">
    <p class="text-kicker uppercase text-accent reveal" appReveal i18n="@@featuresKicker">
      Four tabs
    </p>
    <h2
      class="mt-kicker-gap max-w-[22ch] text-headline text-ink text-balance reveal"
      appReveal
      i18n="@@featuresHeadline"
    >
      Four questions, four screens.
    </h2>
    <p
      class="mt-8 max-w-[58ch] text-body text-ink-2 reveal"
      appReveal
      i18n="@@featuresIntro"
    >
      Everything sits under one of four tabs. Nothing is buried three menus
      deep, and no dashboard tries to say it all at once.
    </p>

    <div class="mt-[112px] space-y-[112px] lg:space-y-[140px]">
      @for (block of blocks; track block.id; let i = $index) {
      <article
        class="grid items-center gap-16 lg:grid-cols-2 lg:gap-24"
      >
        <div
          class="reveal flex justify-center lg:justify-start"
          [class.lg:order-2]="i % 2 === 1"
          appReveal
        >
          <app-device-frame
            class="[--s:0.62] sm:[--s:0.78] xl:[--s:0.88]"
            [label]="block.frameLabel"
          >
            @switch (block.id) { @case ('today') {
            <app-screen-today />
            } @case ('plan') {
            <app-screen-plan />
            } @case ('money') {
            <app-screen-money />
            } @case ('tools') {
            <app-screen-tools />
            } }
          </app-device-frame>
        </div>

        <div class="max-w-[46ch] reveal" appReveal [appReveal]="90">
          <p class="text-kicker uppercase text-accent">{{ block.tab }}</p>

          <h3 class="mt-kicker-gap text-[28px] font-medium leading-[1.2] tracking-[-0.02em] text-ink text-balance">
            {{ block.headline }}
          </h3>

          <p class="mt-6 text-body text-ink-2">{{ block.body }}</p>

          <ul class="mt-8 space-y-4">
            @for (point of block.points; track point.title) {
            <li class="flex gap-6">
              <span
                class="mt-[7px] h-px w-6 flex-none bg-accent-stroke"
                aria-hidden="true"
              ></span>
              <span class="text-label">
                <span class="block text-ink">{{ point.title }}</span>
                <span class="mt-2 block text-ink-2">{{ point.detail }}</span>
              </span>
            </li>
            }
          </ul>
        </div>
      </article>
      }
    </div>
  </div>
</section>
